import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, RefreshCw, Copy, UploadCloud, CheckCircle2 } from 'lucide-react';
import { toast } from '../lib/toast';
import { statusMeta, PAYMENT_METHOD_META } from './organization/venue-booking/bookingStatusMeta';
import { PaymentMethodBadge, PaymentMethodMark } from './organization/venue-booking/PaymentMethodBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const copyText = async (text) => { try { await navigator.clipboard.writeText(text); toast.success('تم النسخ'); } catch { toast.error('تعذّر النسخ'); } };

export default function BookingStatusPage() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/bookings/track/${token}`);
      setBooking(res.data);
    } catch { setNotFound(true); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/public/bookings/track/${token}/payment-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم رفع الإثبات، بانتظار تأكيد الملعب');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل رفع الملف'); } finally { setUploading(false); }
  };

  if (notFound) {
    return (
      <div dir="rtl" className="flex min-h-screen items-center justify-center bg-[#0A192F] px-4 text-center" data-testid="booking-status-not-found">
        <p className="text-slate-300">رابط التتبع غير صحيح أو منتهي</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div dir="rtl" className="flex min-h-screen items-center justify-center bg-[#0A192F]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  const meta = statusMeta(booking.status);
  const methodLabel = booking.payment_method_label || PAYMENT_METHOD_META[booking.payment_method]?.label;

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A192F] px-4 py-10 text-white" style={{ fontFamily: "'Noto Sans Arabic', Arial, sans-serif" }} data-testid="booking-status-page">
      <div className="mx-auto max-w-md space-y-5">
        <div className="text-center">
          <p className="text-xs font-semibold text-slate-400">متابعة الحجز</p>
          <h1 className="mt-1 text-xl font-black">{booking.venue_name} {booking.court_label ? `— ${booking.court_label}` : ''}</h1>
          {booking.booking_number && (
            <span dir="ltr" data-testid="booking-status-number" className="mt-2 inline-block rounded-full bg-white/10 px-3 py-1 text-sm font-mono font-bold text-teal-300">{booking.booking_number}</span>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center" data-testid="booking-status-badge-card">
          <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-bold ${meta.badge}`}><span className={`h-2 w-2 rounded-full ${meta.dot}`} />{meta.label}</span>
          {booking.payment_rejection_reason && (
            <p className="mt-2 text-xs text-red-300" data-testid="booking-status-rejection-reason">سبب رفض الإثبات: {booking.payment_rejection_reason} — يمكنك رفع إثبات جديد أدناه</p>
          )}
        </div>

        <div className="space-y-2 rounded-2xl border border-white/10 bg-white/5 p-5 text-sm" data-testid="booking-status-summary">
          <div className="flex justify-between"><span className="text-slate-400">التاريخ</span><span dir="ltr" className="font-semibold">{booking.date}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">الوقت</span><span dir="ltr" className="font-semibold">{booking.start_time}–{booking.end_time}</span></div>
          {booking.price_per_hour && <div className="flex justify-between"><span className="text-slate-400">السعر</span><span className="font-semibold">{booking.price_per_hour} ر.س/ساعة</span></div>}
          {methodLabel && (
            <div className="flex items-center justify-between">
              <span className="text-slate-400">طريقة الدفع</span>
              {booking.payment_method ? <PaymentMethodBadge methodId={booking.payment_method} testId="booking-status-payment-method" /> : <span className="font-semibold">{methodLabel}</span>}
            </div>
          )}
          {booking.customer_name && <div className="flex justify-between"><span className="text-slate-400">الاسم</span><span className="font-semibold">{booking.customer_name}</span></div>}
        </div>

        {booking.status === 'pending_payment' && (
          <div className="space-y-3 rounded-2xl border border-orange-400/30 bg-orange-500/10 p-5" data-testid="booking-status-payment-section">
            {booking.payment_details && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-orange-300">
                  بيانات {methodLabel}
                  {booking.payment_method && PAYMENT_METHOD_META[booking.payment_method] && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white p-0.5 shadow-sm">
                      <PaymentMethodMark meta={PAYMENT_METHOD_META[booking.payment_method]} className="h-3.5 w-3.5" />
                    </span>
                  )}
                </p>
                <div className="flex items-start justify-between gap-2 rounded-lg bg-white/5 p-3">
                  <p className="whitespace-pre-line text-xs text-slate-200">{booking.payment_details}</p>
                  <button onClick={() => copyText(booking.payment_details)} data-testid="booking-status-copy-details" className="shrink-0 rounded-md bg-white/10 p-1.5 hover:bg-white/20"><Copy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            )}
            {booking.proof_required && (
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-orange-400/40 p-5 text-center transition-colors hover:border-orange-400/70" data-testid="booking-status-upload-proof">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin text-orange-300" /> : <UploadCloud className="h-6 w-6 text-orange-300" />}
                <span className="text-xs font-bold text-orange-200">{uploading ? 'جاري الرفع...' : 'رفع إثبات الدفع (صورة أو PDF)'}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploading} onChange={(e) => uploadProof(e.target.files?.[0])} />
              </label>
            )}
          </div>
        )}

        {booking.status === 'pending_payment_verification' && (
          <div className="flex items-center gap-3 rounded-2xl border border-blue-400/30 bg-blue-500/10 p-5" data-testid="booking-status-verification-info">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-blue-300" />
            <p className="text-xs text-blue-200">تم استلام إثبات دفعك، بانتظار تأكيد الملعب/المنشأة قريباً</p>
          </div>
        )}

        <button onClick={load} data-testid="booking-status-refresh-btn" className="mx-auto flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white">
          <RefreshCw className="h-3.5 w-3.5" /> تحديث الحالة
        </button>
      </div>
    </div>
  );
}
